import { IconButton } from '@openx/design-system/components/Button/IconButton';
import { Checkbox } from '@openx/design-system/components/Checkbox/Checkbox';
import { Menu } from '@openx/design-system/components/Menu/Menu';
import { MenuItem } from '@openx/design-system/components/Menu/MenuItem';
import { CaretDown } from '@phosphor-icons/react';
import { type MouseEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { popoverSx } from './utils/styles';
import type { OpenXAgentSelectionMenuItem } from './utils/types';

type OpenXAgentSelectAllCheckboxProps = {
  itemCount: number;
  selectedCount: number;
  ariaLabel: string;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  menuItems: OpenXAgentSelectionMenuItem[];
};

export const OpenXAgentSelectAllCheckbox = ({
  itemCount,
  selectedCount,
  ariaLabel,
  onSelectAll,
  onDeselectAll,
  menuItems,
}: OpenXAgentSelectAllCheckboxProps) => {
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const isAllSelected = itemCount > 0 && selectedCount === itemCount;
  const isPartiallySelected = selectedCount > 0 && !isAllSelected;

  const openMenu = useCallback((event: MouseEvent<HTMLElement>) => setMenuAnchor(event.currentTarget), []);
  const closeMenu = useCallback(() => setMenuAnchor(null), []);

  return (
    <>
      <Checkbox
        checked={isAllSelected}
        indeterminate={isPartiallySelected}
        disabled={itemCount === 0}
        onChange={isAllSelected ? onDeselectAll : onSelectAll}
        slotProps={{ input: { 'aria-label': ariaLabel } }}
      />

      <IconButton size="small" aria-label={t('Selection options')} aria-haspopup="menu" onClick={openMenu}>
        <CaretDown />
      </IconButton>

      <Menu anchorEl={menuAnchor} open={menuAnchor !== null} onClose={closeMenu} sx={popoverSx}>
        {menuItems.map(({ label, onClick }) => (
          <MenuItem
            key={label}
            onClick={() => {
              onClick();
              closeMenu();
            }}
          >
            {label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
